// standards-ignore: 'Progress' and 'Situation' repeats are the AUDIT_TYPE_PREFIX map itself — the map is the named constant
import { useMemo } from 'react';
import { useCurrentUser } from './UserProvider';
import type { Permission } from 'src/contstant/permissionAccessConfig';

/**
 * Convenience interface exposing high-level capability checks
 * for common mobile actions. Derived from the raw permission set.
 */
export interface AuditPermissions {
  /** Can the user perform (answer questions on) an audit of this type? */
  canPerformAudit: (auditType: string) => boolean;
  /** Can the user close an audit of this type? */
  canCloseAudit: (auditType: string) => boolean;
  /** Can the user reopen an audit of this type? */
  canReopenAudit: (auditType: string) => boolean;
  /** Can the user edit (create, update, cancel) an audit of this type? */
  canEditAudit: (auditType: string) => boolean;
  /** True when the user has at least one Edit*Audit permission (any type). */
  canCreateAnyAudit: boolean;
}

export interface TaskPermissions {
  canEditTask: boolean;
  canPerformTask: boolean;
  canCancelTask: boolean;
  canChangeTaskAssignee: boolean;
  canReopenTask: boolean;
}

export interface UsePermissionsResult {
  /** Raw permission set — use for edge cases not covered by helpers. */
  permissions: ReadonlySet<Permission>;
  /** Audit-type-scoped permission checks. */
  audit: AuditPermissions;
  /** Task permission checks (not audit-type-scoped). */
  task: TaskPermissions;
  /** Whether the user has a specific raw permission string. */
  has: (permission: Permission) => boolean;
}

/**
 * Maps an audit type to the permission prefix.
 * Uses AuditType enum values from the SDK for type safety.
 */
const AUDIT_TYPE_PREFIX: Record<string, string> = {
  progress: 'Progress',
  mpa: 'Mpa',
  test: 'Test',
  calibration: 'Calibration',
  situational: 'Situation',
  situation: 'Situation',
};

/**
 * Canonical list of audit-type keys used to derive permissions.
 * Sourced from AUDIT_TYPE_PREFIX so canCreateAnyAudit stays in sync
 * with the prefix map — adding a new type here automatically
 * extends permission checks.
 */
const AUDIT_TYPES = Object.keys(AUDIT_TYPE_PREFIX);

function auditTypeToPrefix(auditType: string): string {
  return AUDIT_TYPE_PREFIX[auditType.toLowerCase()] ?? 'Progress';
}

/**
 * Hook that provides permission checks derived from the current user's
 * granted permissions. Wraps the raw permission set with semantic helpers
 * so components don't need to know permission string formats.
 *
 * @example
 * ```tsx
 * const { audit, task } = usePermissions();
 *
 * if (audit.canPerformAudit('Progress')) { ... }
 * if (task.canReopenTask) { ... }
 * ```
 */
export function usePermissions(): UsePermissionsResult {
  const { permissions } = useCurrentUser();

  return useMemo(() => {
    const has = (p: Permission): boolean => permissions.has(p);

    const audit: AuditPermissions = {
      canPerformAudit: (auditType) =>
        has(`Perform${auditTypeToPrefix(auditType)}Audit` as Permission),
      canCloseAudit: (auditType) =>
        has(`Close${auditTypeToPrefix(auditType)}Audit` as Permission),
      canReopenAudit: (auditType) =>
        has(`Reopen${auditTypeToPrefix(auditType)}Audit` as Permission),
      canEditAudit: (auditType) =>
        has(`Edit${auditTypeToPrefix(auditType)}Audit` as Permission),
      canCreateAnyAudit: AUDIT_TYPES.some((auditType) =>
        has(`Edit${auditTypeToPrefix(auditType)}Audit` as Permission),
      ),
    };

    const task: TaskPermissions = {
      canEditTask: has('EditTask'),
      canPerformTask: has('PerformTask'),
      canCancelTask: has('CancelTask'),
      canChangeTaskAssignee: has('ChangeTaskAssignee'),
      canReopenTask: has('ReopenTask'),
    };

    return { permissions, audit, task, has };
  }, [permissions]);
}
